import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@/components/ui/Card.tsx';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import Chip from '@/components/ui/Chip.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver';
import { formatDayMonthYear } from '@/utils/dateFormatters.ts';
import { PendingInvitationDto } from '@/features/dashboard/_root/types/PendingInvitationDto.ts';
import { getRoleLabel } from '@/features/auth/login/types/Role.utils.ts';
import { companyInitials } from '@/features/auth/workspaces/utils/companyInitials.ts';
import useAcceptInvitation from '@/features/public/acceptInvitation/hooks/useAcceptInvitation.ts';
import {
  acceptInvitationSchema,
  AcceptInvitationFormData,
} from '@/features/public/acceptInvitation/schemas/acceptInvitationSchema.ts';

const PendingInvitationCard = ({
  invitation,
}: {
  invitation: PendingInvitationDto;
}) => {
  const [open, setOpen] = useState(false);
  const { mutate: acceptInvitation, isPending } = useAcceptInvitation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInvitationFormData>({
    resolver: debugZodResolver(acceptInvitationSchema),
  });

  const onSubmit = (data: AcceptInvitationFormData) =>
    acceptInvitation({
      token: invitation.token,
      firstName: data.firstName,
      lastName: data.lastName,
    });

  return (
    <Card variant="dashed" className="flex w-full flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <InitialsAvatar
          tone="plain"
          initials={companyInitials(invitation.companyName)}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate font-semibold text-base-text">
            {invitation.companyName}
          </span>
          <span className="text-sm text-neutral-500">
            Zaproszenie ważne do {formatDayMonthYear(invitation.expiresAt)}
          </span>
          <Chip label={getRoleLabel(invitation.role)} tone="accent" />
        </div>
        {!open && (
          <ActionButton tone="accent" size="sm" onClick={() => setOpen(true)}>
            Dołącz
          </ActionButton>
        )}
      </div>
      {open && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-4"
        >
          <TextInput
            label="Imię"
            register={register('firstName')}
            placeholder="Wpisz imię"
            required
            error={errors.firstName?.message}
          />
          <TextInput
            label="Nazwisko"
            register={register('lastName')}
            placeholder="Wpisz nazwisko"
            required
            error={errors.lastName?.message}
          />
          <div className="flex gap-2">
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={isPending}
            >
              Dołącz do firmy
            </ActionButton>
            <ActionButton
              tone="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Anuluj
            </ActionButton>
          </div>
        </form>
      )}
    </Card>
  );
};

export default PendingInvitationCard;
